import { Link } from "@frontend/lib/link";
import { ActionForm, SubmitButton } from "@frontend/components/action-form";
import { Disclosure, Field } from "@frontend/components/ui";
import { cancelCollabAction } from "@frontend/lib/actions";
import { publicApiUrl } from "@frontend/lib/api";
import { canCancelCollab, hasNonRefundablePayment } from "@frontend/lib/cancel";
import type { CollabRoom } from "@creators/backend/lib/collabs/queries";
import { PAYMENT_METHOD_LABELS } from "@creators/backend/lib/collabs/payment-methods";
import { isTerminal } from "@creators/backend/lib/collabs/state-machine";
import { formatINR } from "@creators/backend/lib/money";
import { formatDate } from "@creators/backend/lib/time";

type Side = "creator" | "brand" | "admin";

/** Sidebar section shell, matching the panels the collab room already uses. */
export function DetailsSection({
  title,
  count,
  action,
  compact,
  children,
}: {
  title: string;
  count?: number;
  action?: React.ReactNode;
  compact?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="card overflow-hidden">
      <div
        className={
          compact
            ? "flex items-center justify-between gap-2 border-b border-line px-3 py-2"
            : "flex items-center justify-between gap-3 border-b border-line px-4 py-2.5"
        }
      >
        <h2 className="text-sm font-semibold text-ink">
          {title}
          {count !== undefined ? (
            <span className="tabular ml-1.5 font-normal text-ink-subtle">{count}</span>
          ) : null}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** End the collab from Details — same rules as mobile Record. */
export function CancelCollabCard({
  room,
  viewer,
  canAct,
  verified,
}: {
  room: CollabRoom;
  viewer: Side;
  canAct: boolean;
  verified: boolean;
}) {
  if (!room.terms || !canAct || isTerminal(room.collab.status)) return null;
  if (!canCancelCollab({ side: viewer, milestones: room.milestones })) return null;

  const moneyMoved = hasNonRefundablePayment(room.milestones);

  return (
    <DetailsSection title="Cancel">
      <div className="px-4 py-3">
        {verified ? (
          <Disclosure label="Cancel this collab" tone="danger">
            <ActionForm action={cancelCollabAction}>
              <input type="hidden" name="collabId" value={room.collab.id} />
              <Field
                label="Reason"
                optional
                hint={
                  moneyMoved
                    ? "Payments already made are non-refundable and stay with the creator. Unpaid amounts are no longer due."
                    : "Cancelling closes this collab for both sides."
                }
              >
                <textarea
                  name="reason"
                  rows={2}
                  placeholder="Optional — share why if you want"
                  className="field-input resize-y"
                />
              </Field>
              <SubmitButton variant="danger" full pendingLabel="Cancelling…">
                Confirm cancellation
              </SubmitButton>
            </ActionForm>
          </Disclosure>
        ) : (
          <p className="text-sm text-ink-muted">Sign in to cancel this collab.</p>
        )}
      </div>
    </DetailsSection>
  );
}

/**
 * Everything attached to the collab in one place: the signed agreement and any
 * payment proof, so nobody has to scroll the thread to find a receipt.
 */
export function SharedFilesCard({ room }: { room: CollabRoom }) {
  const collabId = room.collab.id;
  const files = [
    ...(room.terms
      ? [
          {
            key: "contract",
            label: "Agreement",
            meta: `Locked ${formatDate(room.terms.acceptedAt)}`,
            href: `/collabs/${collabId}/contract`,
            external: false,
            tone: "bg-brand-50 text-brand-700",
            ext: "PDF",
          },
        ]
      : []),
    ...room.proofs
      .filter((proof) => proof.hasImage)
      .map((proof) => ({
        key: proof.id,
        label: `${formatINR(proof.amount)} proof`,
        meta: `${
          PAYMENT_METHOD_LABELS[proof.method as keyof typeof PAYMENT_METHOD_LABELS] ?? proof.method
        } · ${formatDate(proof.submittedAt)}`,
        href: `${publicApiUrl()}/api/collabs/${collabId}/proofs/${proof.id}/image`,
        external: true,
        tone: "bg-money-50 text-money-700",
        ext: "IMG",
      })),
  ];

  if (files.length === 0) return null;

  return (
    <DetailsSection title="Shared files" count={files.length}>
      <ul className="divide-y divide-line/60">
        {files.map((file) => (
          <li key={file.key}>
            <FileRow {...file} />
          </li>
        ))}
      </ul>
    </DetailsSection>
  );
}

function FileRow({
  label,
  meta,
  href,
  external,
  tone,
  ext,
}: {
  label: string;
  meta: string;
  href: string;
  external: boolean;
  tone: string;
  ext: string;
}) {
  const body = (
    <>
      <span
        className={`inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-[9px] font-bold tracking-wide ${tone}`}
        aria-hidden="true"
      >
        {ext}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-ink">{label}</span>
        <span className="block truncate text-xs text-ink-subtle">{meta}</span>
      </span>
    </>
  );

  const className = "flex items-center gap-3 px-4 py-2.5 transition hover:bg-canvas/60";

  return external ? (
    <a href={href} target="_blank" rel="noreferrer" className={className}>
      {body}
    </a>
  ) : (
    <Link href={href} className={className}>
      {body}
    </Link>
  );
}

/** Draft assets and the live post, pulled out of the delivery history. */
export function SharedLinksCard({ room }: { room: CollabRoom }) {
  const links: { key: string; label: string; url: string }[] = [];

  for (const deliverable of room.deliverables) {
    const urls = Array.isArray(deliverable.assetUrls) ? (deliverable.assetUrls as string[]) : [];
    urls.forEach((url, index) => {
      if (typeof url !== "string" || !url.trim()) return;
      links.push({
        key: `${deliverable.id}-${index}`,
        label: deliverable.kind === "published" ? "Live post" : "Draft asset",
        url,
      });
    });
    if (deliverable.publishedUrl) {
      links.push({
        key: `${deliverable.id}-published`,
        label: "Live post",
        url: deliverable.publishedUrl,
      });
    }
  }

  if (links.length === 0) return null;

  return (
    <DetailsSection title="Shared links" count={links.length}>
      <ul className="divide-y divide-line/60">
        {links.map((link) => (
          <li key={link.key}>
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 px-4 py-2.5 transition hover:bg-canvas/60"
            >
              <span
                className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-canvas text-ink-muted ring-1 ring-line"
                aria-hidden="true"
              >
                <LinkIcon />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-ink">{link.label}</span>
                <span className="block truncate text-xs text-ink-subtle">
                  {displayUrl(link.url)}
                </span>
              </span>
            </a>
          </li>
        ))}
      </ul>
    </DetailsSection>
  );
}

function displayUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname === "/" ? "" : parsed.pathname}`;
  } catch {
    return url;
  }
}

function LinkIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
      <path
        fillRule="evenodd"
        d="M12.586 4.586a2 2 0 1 1 2.828 2.828l-3 3a2 2 0 0 1-2.828 0 1 1 0 0 0-1.414 1.414 4 4 0 0 0 5.656 0l3-3a4 4 0 0 0-5.656-5.656l-1.5 1.5a1 1 0 1 0 1.414 1.414l1.5-1.5Zm-5 5a2 2 0 0 1 2.828 0 1 1 0 1 0 1.414-1.414 4 4 0 0 0-5.656 0l-3 3a4 4 0 1 0 5.656 5.656l1.5-1.5a1 1 0 1 0-1.414-1.414l-1.5 1.5a2 2 0 1 1-2.828-2.828l3-3Z"
        clipRule="evenodd"
      />
    </svg>
  );
}
